// @ts-check
import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import Portal from '@xingternal/malt-portal';
import { Transition } from 'react-transition-group';
import { testable } from '@xingternal/malt-util';
import { QA_MALT } from '@xingternal/malt-constants';
import {
  IconCoreWarning,
  IconShapeTick,
  IconCoreInfo,
} from '@xingternal/malt-icon';
import { Paragraph } from '@xingternal/malt-typography';
import { withFlavour } from '@xingternal/flavour';

// @ts-ignore
import styles from './Feedback.css';

/**
 * @type {any}
 */
const TestableDiv = testable(QA_MALT, 'div');

/**
 * @type import('.').FeedbackType
 */
const Feedback = ({
  action,
  children,
  className,
  containerId,
  isLocal,
  onCancel,
  show,
  slideInDirection = 'bottom',
  testId,
  type = 'error',
  flavour,
}) => {
  let FeedbackIcon = IconCoreWarning;
  switch (type) {
    case 'confirmation':
      FeedbackIcon = IconShapeTick;
      break;
    case 'info':
      FeedbackIcon = IconCoreInfo;
      break;
  }

  const renderFeedBackContent = state => {
    return (
      <TestableDiv
        onClick={onCancel}
        testId={testId}
        className={classnames(
          styles.feedback,
          styles[state],
          styles[slideInDirection],
          styles[action ? 'action' : type],
          {
            [styles.global]: !containerId && !isLocal,
            [styles.local]: isLocal,
            [styles.xdl]: flavour === 'xdl',
          },
          className
        )}
      >
        <div className={styles.contentWrapper}>
          <div className={styles.content}>
            <span>
              <FeedbackIcon size={24} className={styles.Icon} />
            </span>
            <Paragraph noMargin className={styles.paragraph}>
              {children}
            </Paragraph>
          </div>
          {action && <div className={styles.actionWrapper}>{action}</div>}
        </div>
      </TestableDiv>
    );
  };

  if (isLocal) {
    return (
      <Transition mountOnEnter in={show} timeout={100}>
        {renderFeedBackContent}
      </Transition>
    );
  }

  return (
    <Portal containerId={containerId}>
      <Transition mountOnEnter in={show} timeout={100}>
        {renderFeedBackContent}
      </Transition>
    </Portal>
  );
};

Feedback.propTypes = {
  /** is either xdl or odl depending on flavour context  */
  flavour: PropTypes.string,
  /** use feedback in local mode wrapped by FeedbackContainer  */
  isLocal: PropTypes.bool,
  /** show/hide the feedback */
  show: PropTypes.bool,
  /** Content of the feedback */
  children: PropTypes.node,
  /** Adds a class to the feedback */
  className: PropTypes.string,
  /** define a testid for the feedback */
  testId: PropTypes.string,
  /** @private */
  slideInDirection: PropTypes.oneOf(['bottom', 'top']),
  /** define a function to be called when clicking on the close icon */
  onCancel: PropTypes.func,
  /** define the type of feedback */
  type: PropTypes.oneOf(['error', 'confirmation', 'info']),
  /**
   * provide a call to action node as a child. Should be paired with type action
   */
  action: PropTypes.node,
  /**
   * provide a container ID to render the feedback in a
   * specific container instead of appending it to the body
   * @deprecated use isLocal flag instead
   */
  containerId: PropTypes.string,
};

Feedback.displayName = 'Feedback';

export default withFlavour(Feedback);
